/**
 * Full page: continue a client Expression of Interest into an RFQ or RFT.
 */
import React, { useState } from 'react';
import { Alert, Button, Card, Radio, Space, Typography, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CLIENT_INVITATION_NOTE,
  EPC_INVITATION_KIND_LABEL,
  epcInvitationKind,
  type EpcInvitationKind,
} from '@/constants/epcInvitationKind';
import { workflowStagesFor } from '@/constants/epcInvitationWorkflow';
import { epcCommercialService } from '@/services/epcCommercial.service';

const { Title, Text, Paragraph } = Typography;

const EpcEoiContinuePage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [kind, setKind] = useState<Extract<EpcInvitationKind, 'RFQ' | 'RFT'>>('RFT');

  const detailQ = useQuery({
    queryKey: ['epc-prequalification', id],
    queryFn: () => epcCommercialService.getPrequalification(id),
    enabled: Boolean(id),
  });

  const row = (detailQ.data || null) as Record<string, unknown> | null;
  const isEoi = epcInvitationKind(row?.invitation_kind || row?.invitationKind) === 'EOI';
  const blocked = String(row?.tender_gate || row?.tenderGate || '') === 'BLOCK';
  const shortlisted = String(row?.eoi_outcome || row?.status || '') === 'Shortlisted';
  const opportunityId = String(row?.opportunity_id || row?.opportunityId || '');

  const continueM = useMutation({
    mutationFn: () => epcCommercialService.continueEoi(id, kind),
    onSuccess: (created) => {
      message.success(`${EPC_INVITATION_KIND_LABEL[kind]} opened`);
      qc.invalidateQueries({ queryKey: ['epc-tenders'] });
      qc.invalidateQueries({ queryKey: ['epc-prequalifications'] });
      const nextId = String(created?.id || '');
      navigate(nextId ? `/epc/commercial/tenders/${encodeURIComponent(nextId)}` : '/epc/commercial/tenders');
    },
    onError: (e: Error) => message.error(e.message || 'Could not continue'),
  });

  return (
    <div style={{ padding: 24, maxWidth: 860, margin: '0 auto' }}>
      <Space align="start" style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          aria-label="Back to expression of interest"
          onClick={() => navigate(`/epc/commercial/prequalifications/${encodeURIComponent(id)}`)}
        />
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Continue invitation
          </Title>
          <Text type="secondary">
            {row ? String(row.prequal_number || row.title || '') : id}
          </Text>
        </div>
      </Space>

      {detailQ.isLoading ? <Card loading /> : null}
      {detailQ.isError ? (
        <Alert type="error" showIcon message={(detailQ.error as Error).message || 'Could not load the EOI'} />
      ) : null}

      {row && !isEoi ? (
        <Alert
          type="error"
          showIcon
          message="Only an Expression of Interest continues into an RFQ or RFT. An internal prequalification gate stays on this register."
        />
      ) : null}

      {row && isEoi ? (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert type="info" showIcon message={CLIENT_INVITATION_NOTE} />
          {!opportunityId ? (
            <Alert type="error" showIcon message="Link an opportunity on the Expression of Interest before continuing." />
          ) : null}
          {!shortlisted ? (
            <Alert
              type="warning"
              showIcon
              message="This Expression of Interest is not shortlisted. You can still continue. Record the reason in the dossier."
            />
          ) : (
            <Alert type="success" showIcon message="Shortlisted. Qualification evidence is carried onto the next invitation." />
          )}
          {blocked ? (
            <Alert type="error" showIcon message="Tender gate is Block. The next invitation cannot be opened until the gate changes." />
          ) : null}

          <Card size="small" title="Next invitation">
            <Radio.Group value={kind} onChange={(e) => setKind(e.target.value)}>
              <Space direction="vertical">
                <Radio value="RFQ">
                  {EPC_INVITATION_KIND_LABEL.RFQ}
                  <Paragraph type="secondary" style={{ margin: '4px 0 0 24px' }}>
                    {workflowStagesFor('RFQ').join(' → ')}
                  </Paragraph>
                </Radio>
                <Radio value="RFT">
                  {EPC_INVITATION_KIND_LABEL.RFT}
                  <Paragraph type="secondary" style={{ margin: '4px 0 0 24px' }}>
                    {workflowStagesFor('RFT').join(' → ')}
                  </Paragraph>
                </Radio>
              </Space>
            </Radio.Group>
          </Card>

          <Card size="small" title="Carried forward">
            <Text>Client, subject, scope, qualification dossier, and the predecessor link stay with the pursuit. The Expression of Interest remains as history. One invitation on the opportunity stays current.</Text>
          </Card>

          <Space>
            <Button
              type="primary"
              loading={continueM.isPending}
              disabled={!opportunityId || blocked}
              onClick={() => continueM.mutate()}
            >
              Open {EPC_INVITATION_KIND_LABEL[kind]}
            </Button>
            <Button onClick={() => navigate(`/epc/commercial/prequalifications/${encodeURIComponent(id)}`)}>
              Cancel
            </Button>
          </Space>
        </Space>
      ) : null}
    </div>
  );
};

export default EpcEoiContinuePage;
